/**
 * The Done stage (plan docs/plans/lms-plan.md 7.2, 7.4, 10.1, 10.3, 10.4, R9,
 * R24; WP-40): the score, the completion date, the expiry date ("Does not
 * expire" when the course has no validity), the certificate code with a
 * Download certificate button, and a Back to Home link.
 *
 * - Download: GET /certificates/:id/url returns a presigned URL (10 minutes,
 *   10.4), never fetched into the page. The new tab is opened SYNCHRONOUSLY
 *   on the click (openPendingTab), inside the click's user activation, and
 *   pointed at the URL when the answer arrives (plan 18.27): a popup opened
 *   only after the awaited request can be blocked (Safari, and Chrome after a
 *   slow answer), and the button then seemed to do nothing. The 'noopener'
 *   feature string cannot be used for this, because window.open then returns
 *   null by specification and leaves nothing to point at the URL; the same
 *   protection is applied by hand instead, severing the new tab's opener
 *   before anything loads in it (a tab whose opener cannot be severed is
 *   closed). When the browser blocks the tab, a visible link to the
 *   presigned URL is shown (target _blank, rel noopener noreferrer); when the
 *   request fails, the tab is closed and the error is shown.
 * - A completion whose certificate did not render (10.3: the completion stands,
 *   certificate_id NULL, the daily pass retries) shows "Certificate pending"
 *   with Retry (POST /completions/:id/certificate/retry).
 * - A competency course whose observed sign-off has not landed yet (7.4)
 *   shows that the quiz half is done and offers "Request a sign-off" (any lms
 *   admin may observe; the request names no observer).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Award, CheckCircle2, Download, Home, RefreshCw } from 'lucide-react';
import { apiErrorCode, apiErrorMessage } from '../../api/client';
import { getCertificateUrl, requestSignoff, retryCertificate } from '../../api/completions';
import type { CertificateRef, CompletionResult } from '../../api/completions';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { headingCls, microLabelCls, noticeCls, panelCls, pillCls, stageCls } from './styles';

/**
 * A blank tab opened inside the click's user activation, its opener severed
 * at once (the noopener protection, 10.4). null when the browser blocked it,
 * or when the opener could not be severed (the tab is then closed).
 */
export function openPendingTab(): Window | null {
  let tab: Window | null = null;
  try {
    tab = window.open('', '_blank');
  } catch {
    return null;
  }
  if (!tab) return null;
  try {
    tab.opener = null;
  } catch {
    tab.close();
    return null;
  }
  return tab;
}

export interface DoneStageProps {
  courseId: string;
  courseTitle: string;
  result: CompletionResult;
  /** 10.1: whether this completion earns a certificate (issue_certificate, category). */
  expectsCertificate: boolean;
}

/** d MMM yyyy (Appendix F's date format). */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'd MMM yyyy');
}

export function DoneStage({ courseId, courseTitle, result, expectsCertificate }: DoneStageProps) {
  const { toast } = useToast();
  const [certificate, setCertificate] = useState<CertificateRef | null>(result.certificate ?? null);
  const [downloading, setDownloading] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [signoffState, setSignoffState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [signoffMessage, setSignoffMessage] = useState<string | null>(null);
  const completion = result.completion;

  const download = async () => {
    if (!certificate || downloading) return;
    // Before any await: the tab is opened while the click's user activation lasts.
    const tab = openPendingTab();
    setDownloading(true);
    setBlockedUrl(null);
    try {
      const { url } = await getCertificateUrl(certificate.id, 'attachment');
      if (tab && !tab.closed) tab.location.replace(url);
      else setBlockedUrl(url);
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      toast(apiErrorMessage(err, 'The certificate could not be downloaded. Try again.'), { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const retry = async () => {
    if (!completion || retrying) return;
    setRetrying(true);
    try {
      const out = await retryCertificate(completion.id);
      if (out?.certificate) {
        setCertificate({ id: out.certificate.id, code: out.certificate.code });
        toast('Your certificate is ready.', { type: 'success' });
      }
    } catch (err) {
      toast(apiErrorMessage(err, 'The certificate is still pending. It will be retried automatically.'), { type: 'error' });
    } finally {
      setRetrying(false);
    }
  };

  const askSignoff = async () => {
    if (signoffState !== 'idle') return;
    setSignoffState('sending');
    setSignoffMessage(null);
    try {
      await requestSignoff(courseId);
      setSignoffState('sent');
      setSignoffMessage('Sign-off requested. A Learning Center admin will observe you and record it.');
    } catch (err) {
      setSignoffState('idle');
      setSignoffMessage(
        apiErrorCode(err) === 'LMS_CONFLICT'
          ? 'A sign-off request for this course is already open.'
          : apiErrorMessage(err, 'The sign-off request could not be sent. Try again.')
      );
    }
  };

  // 7.4: the quiz half of a competency course is done; the observed sign-off is still needed.
  if (!completion && result.signoff_required) {
    return (
      <div className={stageCls} data-testid="lms-done-stage">
        <div className={`${panelCls} space-y-4`}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={headingCls}>Quiz passed: a sign-off is next</h2>
            <span className={pillCls('amber')}>Sign-off needed</span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {`${courseTitle} is a clinical skill. It is complete once a qualified observer has watched you perform the listed steps and signed them off.`}
          </p>
          <div aria-live="polite">
            {signoffMessage ? (
              <div className={noticeCls(signoffState === 'sent' ? 'green' : 'amber')} data-testid="lms-signoff-message">
                {signoffMessage}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => void askSignoff()}
              disabled={signoffState !== 'idle'}
              data-testid="lms-request-signoff"
            >
              {signoffState === 'sent' ? 'Sign-off requested' : 'Request a sign-off'}
            </button>
            <Link to="/lms" className={secondaryBtnCls}>
              <Home className="h-4 w-4" aria-hidden="true" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const readAck = completion?.kind === 'read_ack';
  return (
    <div className={stageCls} data-testid="lms-done-stage">
      <div className={`${panelCls} space-y-5`}>
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden="true" />
          <h2 className={headingCls}>{readAck ? 'Acknowledgement recorded' : 'Training complete'}</h2>
          <span className={pillCls('green')}>Complete</span>
        </div>

        <dl className="grid gap-4 sm:grid-cols-3" data-testid="lms-done-facts">
          {!readAck && typeof completion?.score_pct === 'number' && (
            <div>
              <dt className={microLabelCls}>Score</dt>
              <dd className="mt-1 text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-done-score">
                {`${completion.score_pct}%`}
              </dd>
            </div>
          )}
          <div>
            <dt className={microLabelCls}>Completed on</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDay(completion?.completed_at)}</dd>
          </div>
          <div>
            <dt className={microLabelCls}>Valid until</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100" data-testid="lms-done-expiry">
              {completion?.expires_at ? formatDay(completion.expires_at) : 'Does not expire'}
            </dd>
          </div>
        </dl>

        {certificate ? (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700" data-testid="lms-done-certificate">
            <Award className="h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className={microLabelCls}>{readAck ? 'Acknowledgement record' : 'Certificate'}</div>
              <div className="font-mono text-sm text-gray-900 dark:text-gray-100" data-testid="lms-certificate-code">
                {certificate.code}
              </div>
            </div>
            <button type="button" className={primaryBtnCls} onClick={() => void download()} disabled={downloading} data-testid="lms-download-certificate">
              <Download className="h-4 w-4" aria-hidden="true" />
              {downloading ? 'Preparing...' : 'Download certificate'}
            </button>
          </div>
        ) : expectsCertificate && completion ? (
          <div className={noticeCls('amber')} data-testid="lms-certificate-pending">
            <div className="min-w-0 flex-1 space-y-2">
              <div>Certificate pending. Your completion is recorded; the certificate will follow.</div>
              <button type="button" className={outlineBtnCls()} onClick={() => void retry()} disabled={retrying} data-testid="lms-certificate-retry">
                <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" />
                Retry
              </button>
            </div>
          </div>
        ) : null}

        {certificate && blockedUrl ? (
          <div className={noticeCls('amber')} role="status" data-testid="lms-certificate-blocked">
            <div className="min-w-0 flex-1 space-y-1">
              <div>Your browser blocked the new tab. Use this link instead; it works for 10 minutes.</div>
              <a
                href={blockedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary-600 hover:underline dark:text-primary-400"
                data-testid="lms-certificate-link"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download certificate
              </a>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Link to="/lms" className={secondaryBtnCls} data-testid="lms-done-home">
            <Home className="h-4 w-4" aria-hidden="true" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DoneStage;
